/** @jsxImportSource @emotion/react */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import VocabularyCard from '@component/iyagigo/vocabulary-book/vocabulary-card';
import BottomContainer from '@component/common/architecture/bottom-container';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import TextButton from '@design-system/text-button';
import ImageAudioButton from '@design-system/image-audio-button';
import IconButton from '@design-system/icon-button';
import Callout from '@design-system/callout';
import Skeleton from '@design-system/skeleton';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import * as color from '@constant/color';
import * as size from '@constant/size';
import {
  EApiLanguageCode,
  EApiStudyType,
  EApiVocabularyBookCategory,
  EApiVocabularyBookLevel,
  EVocabularyGrade,
} from '@datatype/iyagigo/api-type';
import {
  EVocabularyBookStudyType,
  EWorkbookItemType,
  TIyagigoSubMenu,
  VOCABULARY_BOOK_LEVEL_LABEL,
} from '@datatype/iyagigo/type';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { useModifyVocabularyWorkbookMutation } from '@hook/iyagigo/useApi';
import { IVocabularyWorkbookData } from '@datatype/iyagigo/api-type';
import { useCheckAttendance, useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { usePlaySound } from '@hook/common/useSound';
import { getAttendanceType } from '@util/attendance';
import { calculateVocabularyBookReviewAfter } from '@util/workbook';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';

interface IProps {
  vocabularyBookKey: string;
  workbooks: IVocabularyWorkbookData[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  isReady: boolean;
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

// 웹은 BottomContentContainer의 하단 padding(MARGIN_4)만큼 높이를 더 확보 — 안 그러면 컨텐츠가 위로 넘쳐 카드와 겹침
const BOTTOM_CONTAINER_HEIGHT = IS_IYAGIGO_MOBILE_VIEW ? '85px' : `calc(85px + ${size.MARGIN_4})`;

const CardContentContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const FirstRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const LeftControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const RightControlContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 2px;
`;

const BookmarkCalloutContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 5px);
  right: 0;
  z-index: 10;
  width: max-content;
`;

const ChoiceRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

// 캡션(다음 복습 시점) + grade 버튼을 한 묶음으로 세로 정렬한 선택지 컨테이너
// (캡션/안내 문구는 공용 TextBlock 사용 — SRS 간격: HARD 당일 / NORMAL 3일 / EASY 7일)
const ChoiceContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const VocabularyBookStudy: React.FC<IProps> = ({
  vocabularyBookKey,
  workbooks,
  currentIndex,
  setCurrentIndex,
  isReady,
}) => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const playSound = usePlaySound();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const vocabularyBookStudyType =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.vocabularyBookStudyType) ??
    EVocabularyBookStudyType.BOOK_STUDY;
  const vocabularyBookItemType =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.vocabularyBookItemType) ??
    EWorkbookItemType.BUNDLE;
  const currentNoteWorkbookKey =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.currentNoteWorkbookKey) ?? null;

  const isSingleItem = vocabularyBookItemType === EWorkbookItemType.SINGLE;
  const isBundleItem = vocabularyBookItemType === EWorkbookItemType.BUNDLE;
  const isVocabularyBookNote =
    vocabularyBookStudyType === EVocabularyBookStudyType.BOOKMARK_NOTE ||
    vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE;
  const isReviewStudy = vocabularyBookStudyType === EVocabularyBookStudyType.REVIEW;
  const isRepeatStudy = isVocabularyBookNote && isBundleItem;
  const isBookStudy = vocabularyBookStudyType === EVocabularyBookStudyType.BOOK_STUDY;

  const vocabularyBookSubMenu = useTypedSelector(
    (state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu,
  ) as TIyagigoSubMenu['vocabulary-book'];
  // 시험단어집(TEST) 미노출 — 카테고리는 STUDY 고정. subMenu는 단어집/단어노트 탭 축이라 카테고리를 결정하지 않음.
  // const vocabularyBookType =
  //   vocabularyBookSubMenu === 'vocabulary-book-study'
  //     ? EApiVocabularyBookCategory.STUDY
  //     : EApiVocabularyBookCategory.TEST;
  const vocabularyBookType = EApiVocabularyBookCategory.STUDY;
  const apiLanguageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

  // Redux (studentReducer)에서 언어별 STUDY 레벨 매칭. TEST 재노출 시 카테고리 분기 부활.
  const currentEnglishStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishStudyVocabularyBookLevel,
  );
  const currentJapaneseStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseStudyVocabularyBookLevel,
  );
  const currentLevel =
    apiLanguageCode === EApiLanguageCode.ENGLISH
      ? currentEnglishStudyVocabularyBookLevel
      : currentJapaneseStudyVocabularyBookLevel;
  const levelLabel = currentLevel ? VOCABULARY_BOOK_LEVEL_LABEL[currentLevel] : '';
  const checkAttendance = useCheckAttendance();
  const recordStudy = useRecordStudy();
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(true);
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';

  useEffect(() => {
    dispatch(modalActions.hideOverlay());
  }, []);

  // ############### api ###############
  const mutation = useModifyVocabularyWorkbookMutation(
    apiLanguageCode,
    vocabularyBookKey,
    vocabularyBookType,
    currentLevel as EApiVocabularyBookLevel,
  );
  const isLoading = !isReady;

  const totalCount = workbooks.length;

  const [isAnswerView, setIsAnswerView] = useState<boolean>(false);
  const [isBookmarkToolTip, setIsBookmarkToolTip] = useState<boolean>(false);
  const { hardCount, normalCount, easyCount } = useMemo(
    () =>
      workbooks.reduce(
        (counts, workbook) => {
          if (workbook.grade === EVocabularyGrade.HARD) counts.hardCount += 1;
          if (workbook.grade === EVocabularyGrade.NORMAL) counts.normalCount += 1;
          if (workbook.grade === EVocabularyGrade.EASY) counts.easyCount += 1;
          return counts;
        },
        { hardCount: 0, normalCount: 0, easyCount: 0 },
      ),
    [workbooks],
  );

  const isLastWord = currentIndex === totalCount - 1;

  const currentWorkbook =
    isSingleItem && currentNoteWorkbookKey
      ? (workbooks.find((item) => item.key === currentNoteWorkbookKey) ?? workbooks[currentIndex])
      : workbooks[currentIndex];
  const currentAiVocabulary = currentWorkbook?.aiVocabulary;
  const isBookmarked = !!currentWorkbook?.isBookmark;

  const handleExplanationClick = () => {
    dispatch(
      modalActions.showNotificationToast({
        type: 'vocabulary-explanation',
        data: {
          aiVocabulary: currentAiVocabulary,
        },
      }),
    );
  };

  const handleBookmarkToggle = () => {
    if (!isAuthenticated) {
      dispatch(
        modalActions.showWallToast({
          type: 'signup-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }
    if (!currentWorkbook?.key) return;
    mutation.mutate({
      workbookKey: currentWorkbook.key,
      isBookmark: !isBookmarked,
    });
  };

  const handleCardClick = () => {
    // 카드 탭으로 정답 보기 비활성화 — 알아요/몰라요/헷갈려요 클릭으로만 정답 노출
  };

  const goNext = () => {
    // 단건(SINGLE) 모드: 노트 단건 진입은 채점 후 바로 종료 (다음 단어로 진행 X)
    if (isSingleItem) {
      return;
    }
    if (isLastWord) {
      checkAttendance(getAttendanceType(studyLanguage, 'vocabulary-book'));
      dispatch(
        modalActions.showContentModal({
          type: 'study-celebration',
          data: {
            title: '오늘도 해냈어요!',
            description: `<strong>${totalCount}개 단어</strong> 학습을 끝까지 마쳤어요`,
          },
        }),
      );
      return;
    }

    // 비회원 quota 가드는 책 단위 잠금(home의 isBookLocked)으로 대체 — 1일차는 끝까지 무료
    // const nextSolvedNumber = currentIndex + 2;
    // if (!isAuthenticated && isBookStudy && nextSolvedNumber > GUEST_VOCABULARY_BOOK_QUOTA) {
    //   dispatch(
    //     modalActions.showWallToast({
    //       type: 'signup-wall',
    //       data: { trigger: 'quota' },
    //     }),
    //   );
    //   return;
    // }

    setCurrentIndex(currentIndex + 1);
    setIsAnswerView(false);
  };

  const recordGrade = (grade: EVocabularyGrade) => {
    if (!currentWorkbook?.key) return;
    recordStudy({
      studentKey,
      studyType: EApiStudyType.VOCABULARY_BOOK,
      languageCode: apiLanguageCode,
      studyTime: getActiveTime(),
      studyCount: 1,
    });
    resetActiveTime();
    mutation.mutate({
      workbookKey: currentWorkbook.key,
      grade,
      reviewAfter: calculateVocabularyBookReviewAfter(grade),
    });
  };

  const handleKnownClick = () => {
    playSound('correct');
    recordGrade(EVocabularyGrade.EASY);
    setIsAnswerView(true);
  };

  const handleUnsureClick = () => {
    playSound('incorrect');
    recordGrade(EVocabularyGrade.NORMAL);
    setIsAnswerView(true);
  };

  const handleUnknownClick = () => {
    playSound('incorrect');
    recordGrade(EVocabularyGrade.HARD);
    setIsAnswerView(true);
  };

  return (
    <Wrapper>
      <CardContentContainer>
        {isLoading || !currentAiVocabulary ? (
          <Skeleton width='100%' height='100%' borderRadius={size.BORDER_RADIUS_2} />
        ) : (
          <VocabularyCard
            aiVocabulary={currentAiVocabulary}
            levelLabel={levelLabel}
            isAnswerView={isAnswerView}
            handleClick={handleCardClick}
          />
        )}
      </CardContentContainer>
      <Gap height={size.MARGIN_1} />
      <BottomContainer
        position='relative'
        height={BOTTOM_CONTAINER_HEIGHT}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={currentWorkbook?.key}>
        <BottomContentContainer>
          {isAnswerView && (
            <FirstRow>
              <LeftControlContainer>
                <ImageAudioButton
                  theme={ETheme.BROWN}
                  tone='normal'
                  kind='outlined'
                  size='medium'
                  shape='square'
                  width='48px'
                  sources={currentAiVocabulary.sourceAudioLink?.[0] ? [currentAiVocabulary.sourceAudioLink[0]] : []}
                />
                <IconButton
                  icon='text-snippet'
                  theme={ETheme.BROWN}
                  tone='normal'
                  kind='outlined'
                  size='medium'
                  shape='square'
                  width='48px'
                  handleClick={handleExplanationClick}
                />
              </LeftControlContainer>
              <RightControlContainer>
                <IconTextBlock
                  icon='bookmark'
                  text={isBookmarked ? '저장완료' : '저장하기'}
                  arrangement='icon-text'
                  design={{
                    box: {
                      event: { cursor: 'pointer' },
                      width: '80px',
                      alignX: 'left',
                      alignY: 'bottom',
                    },
                    text: {
                      size: 'medium',
                      color: isBookmarked ? color.YELLOW_1 : color.BROWN_2,
                    },
                    icon: {
                      size: 'medium',
                      color: isBookmarked ? color.YELLOW_1 : color.BROWN_2,
                      kind: isBookmarked ? 'filled' : 'outlined',
                    },
                  }}
                  click={{ handler: handleBookmarkToggle }}
                />
                <IconBlock
                  icon='info'
                  design={{
                    icon: { size: 'small', color: color.GRAY_2 },
                    box: { event: { cursor: 'pointer' } },
                  }}
                  click={{ handler: () => setIsBookmarkToolTip((prev) => !prev) }}
                />
                {isBookmarkToolTip && (
                  <BookmarkCalloutContainer>
                    <Callout
                      text='저장한 단어 노트에서 다시 볼 수 있어요'
                      direction='bottom'
                      size='tiny'
                      theme={ETheme.GRAY}
                    />
                  </BookmarkCalloutContainer>
                )}
              </RightControlContainer>
            </FirstRow>
          )}
          {!isAnswerView && (
            <TextBlock
              text='이 단어, 언제 다시 볼까요?'
              design={{
                text: { size: 'tiny', color: color.GRAY_1 },
                box: { width: '100%', alignX: 'center' },
              }}
            />
          )}
          {!isAnswerView && (
            <ChoiceRow>
              <ChoiceContainer>
                <TextBlock
                  text='오늘 또'
                  design={{ text: { size: 'tiny', color: color.BROWN_2, bold: true }, box: {} }}
                />
                <TextButton
                  text='몰라요'
                  theme={ETheme.RED}
                  size='medium'
                  shape='pill'
                  width='100%'
                  handleClick={handleUnknownClick}
                />
              </ChoiceContainer>
              <ChoiceContainer>
                <TextBlock
                  text='3일 뒤 또'
                  design={{ text: { size: 'tiny', color: color.BROWN_2, bold: true }, box: {} }}
                />
                <TextButton
                  text='헷갈려요'
                  theme={ETheme.YELLOW}
                  size='medium'
                  shape='pill'
                  width='100%'
                  handleClick={handleUnsureClick}
                />
              </ChoiceContainer>
              <ChoiceContainer>
                <TextBlock
                  text='7일 뒤 또'
                  design={{ text: { size: 'tiny', color: color.BROWN_2, bold: true }, box: {} }}
                />
                <TextButton
                  text='알아요'
                  theme={ETheme.BLUE}
                  size='medium'
                  shape='pill'
                  width='100%'
                  handleClick={handleKnownClick}
                />
              </ChoiceContainer>
            </ChoiceRow>
          )}
          {isAnswerView && (
            <ChoiceRow>
              <TextButton
                text={isLastWord ? '끝내기' : '다음 단어'}
                theme={ETheme.BROWN}
                size='medium'
                shape='pill'
                width='100%'
                handleClick={goNext}
              />
            </ChoiceRow>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default VocabularyBookStudy;
